/**
 * OrgPerformanceDashboard Component
 * Organization-wide performance dashboard for account admins
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Building2,
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  Target,
  BarChart3,
} from 'lucide-react';
import { Card } from '../../common';
import { PDFExportButton } from '../PDFExportButton';
import { PerformanceTrendChart } from '../PerformanceTrendChart';
import { DepartmentPerformanceChart } from './DepartmentPerformanceChart';
import { ManagerPerformanceTable } from './ManagerPerformanceTable';
import { PerformanceHeatmap } from './PerformanceHeatmap';
import { UserPerformanceSearch, type UserSearchResult } from './UserPerformanceSearch';
import { useOrganizationPerformance, usePerformancePermissions } from '../../../hooks';
import { userService } from '../../../services/user.service';
import { useAuth } from '../../../context/AuthContext';
import type { PerformanceQueryParams } from '../../../types';
import { getScoreColor } from '../../../types';

export interface OrgPerformanceDashboardProps {
  /** Period chosen by the page's master control. */
  queryParams: PerformanceQueryParams;
  onDepartmentClick?: (departmentId: string) => void;
  onManagerClick?: (managerId: string) => void;
  onUserSearch?: (query: string) => Promise<UserSearchResult[]>;
}

export const OrgPerformanceDashboard: React.FC<OrgPerformanceDashboardProps> = ({
  queryParams,
  onDepartmentClick,
  onManagerClick,
  onUserSearch,
}) => {
  const { user } = useAuth();
  const permissions = usePerformancePermissions();
  const periodType = queryParams.periodType ?? 'quarterly';
  const [activeView, setActiveView] = useState<'overview' | 'departments' | 'managers' | 'search'>('overview');

  // Fetch organization performance
  const { data: orgData, isLoading } = useOrganizationPerformance(queryParams);

  // Search the tenant's users. The fallback used to return [] and no caller passed a handler,
  // so the Search view answered "No users found" to every query.
  const handleUserSearch = useCallback(async (query: string): Promise<UserSearchResult[]> => {
    if (onUserSearch) {
      return onUserSearch(query);
    }
    const { users } = await userService.listUsers({ search: query, limit: 10 });
    return users.map((u) => ({
      id: u.id,
      name: `${u.firstName} ${u.lastName}`.trim(),
      email: u.email,
      department: u.department ?? undefined,
      role: u.role,
    }));
  }, [onUserSearch]);

  // Calculate trend from trend data
  const trendDirection = useMemo(() => {
    if (!orgData?.trend || orgData.trend.length < 2) return 'stable';
    const latest = orgData.trend[orgData.trend.length - 1]?.score || 0;
    const previous = orgData.trend[orgData.trend.length - 2]?.score || 0;
    if (latest > previous) return 'up';
    if (latest < previous) return 'down';
    return 'stable';
  }, [orgData]);

  // Calculate total employees from distribution
  const totalEmployees = orgData?.scoreDistribution
    ? orgData.scoreDistribution.excellent +
      orgData.scoreDistribution.good +
      orgData.scoreDistribution.needsImprovement +
      orgData.scoreDistribution.poor
    : orgData?.totalEmployees || 0;

  const views = [
    { id: 'overview' as const, label: 'Overview', icon: BarChart3 },
    { id: 'departments' as const, label: 'Departments', icon: Building2 },
    { id: 'managers' as const, label: 'Managers', icon: Users },
    { id: 'search' as const, label: 'Search', icon: Target },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Organization Performance</h2>
          <p className="text-sm text-stone-500">
            View performance metrics across the entire organization
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {permissions.canExport && (
            <PDFExportButton
              type="organization"
              data={orgData || null}
              periodType={periodType}
              tenantName={user?.organizationName || orgData?.tenantName}
              disabled={isLoading || !orgData}
              variant="secondary"
              size="md"
            />
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Organization Average */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">Org Average</p>
              <div className="flex items-center gap-2">
                <span
                  className="text-2xl font-bold"
                  style={{ color: getScoreColor(orgData?.averageScore || 0) }}
                >
                  {isLoading ? '-' : Math.round(orgData?.averageScore || 0)}
                </span>
                {!isLoading && trendDirection === 'up' && (
                  <TrendingUp className="w-4 h-4 text-green-600" />
                )}
                {!isLoading && trendDirection === 'down' && (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                )}
                {!isLoading && trendDirection === 'stable' && (
                  <Minus className="w-4 h-4 text-stone-400" />
                )}
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-500" />
            </div>
          </div>
        </Card>

        {/* Total Employees */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">Total Employees</p>
              <span className="text-2xl font-bold text-stone-800">
                {isLoading ? '-' : totalEmployees}
              </span>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-500" />
            </div>
          </div>
        </Card>

        {/* Departments */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">Departments</p>
              <span className="text-2xl font-bold text-stone-800">
                {isLoading ? '-' : orgData?.departmentBreakdown?.length || 0}
              </span>
            </div>
            <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-teal-500" />
            </div>
          </div>
        </Card>

        {/* Active OKRs */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">Active OKRs</p>
              <span className="text-2xl font-bold text-stone-800">
                {isLoading ? '-' : orgData?.activeOKRs || 0}
              </span>
            </div>
            <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
              <Target className="w-6 h-6 text-amber-500" />
            </div>
          </div>
        </Card>
      </div>

      {/* View Tabs */}
      <div className="border-b border-stone-200">
        <nav className="flex gap-4">
          {views.map((view) => {
            const Icon = view.icon;
            return (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeView === view.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {view.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* View Content */}
      {activeView === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Distribution */}
          <PerformanceHeatmap
            distribution={orgData?.scoreDistribution || { excellent: 0, good: 0, needsImprovement: 0, poor: 0 }}
            totalCount={totalEmployees}
            isLoading={isLoading}
          />

          {/* Trend Chart */}
          <PerformanceTrendChart
            data={orgData?.trend || []}
            title="Performance Trend"
            isLoading={isLoading}
          />

          {/* Top Departments */}
          {orgData?.departmentBreakdown && orgData.departmentBreakdown.length > 0 && (
            <DepartmentPerformanceChart
              departments={orgData.departmentBreakdown.slice(0, 5)}
              title="Top Departments"
              onDepartmentClick={onDepartmentClick}
              isLoading={isLoading}
              className="lg:col-span-2"
            />
          )}
        </div>
      )}

      {activeView === 'departments' && (
        <DepartmentPerformanceChart
          departments={orgData?.departmentBreakdown || []}
          title="All Departments"
          onDepartmentClick={onDepartmentClick}
          isLoading={isLoading}
        />
      )}

      {activeView === 'managers' && (
        <ManagerPerformanceTable
          managers={orgData?.managerBreakdown || []}
          onManagerClick={onManagerClick}
          isLoading={isLoading}
        />
      )}

      {activeView === 'search' && (
        <UserPerformanceSearch
          onSearch={handleUserSearch}
          queryParams={queryParams}
        />
      )}
    </div>
  );
};

export default OrgPerformanceDashboard;
